import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/routes";
import { useAuth } from "@/hooks/use-auth";
import { authenticatedFetch } from "@/lib/queryClient";
import type {
  Empresa,
  UserProfile,
  InviteCode,
  UserInvite,
  Veiculo,
} from "@shared/schema";
// LaudoLegal types not in ReMonta schema — replaced with any until Phase 4 refactor
type Laudo = any;
type LaudoWithDetails = any;
type ConfiguracoesVistoriadora = any;
type Notificacao = any;

function useAuthFetch() {
  return async (url: string) => {
    const res = await authenticatedFetch(url);
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.message || `Erro: ${res.status}`);
    }
    return res.json();
  };
}

export function useEmpresas() {
  const authFetch = useAuthFetch();
  return useQuery<Empresa[]>({
    queryKey: [api.empresas.list.path],
    queryFn: () => authFetch(api.empresas.list.path),
  });
}

export function useEmpresa(id?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Empresa>({
    queryKey: [api.empresas.get.path, id],
    queryFn: () => authFetch(`/api/empresas/${id}`),
    enabled: !!id,
  });
}

export function useParceirosByVistoriadora(vistoriadoraId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Empresa[]>({
    queryKey: [api.empresas.parceiros.path, vistoriadoraId],
    queryFn: () => authFetch(`/api/empresas/${vistoriadoraId}/parceiros`),
    enabled: !!vistoriadoraId,
  });
}

export function useUsuarios(
  empresaId?: string,
  enabled = true,
  vistoriadoraId?: string,
) {
  const authFetch = useAuthFetch();
  return useQuery<UserProfile[]>({
    queryKey: [api.usuarios.list.path, empresaId, vistoriadoraId],
    queryFn: () => {
      let url: string = api.usuarios.list.path;
      if (vistoriadoraId) {
        url = `${api.usuarios.list.path}?vistoriadoraId=${vistoriadoraId}`;
      } else if (empresaId) {
        url = `${api.usuarios.list.path}?empresaId=${empresaId}`;
      }
      return authFetch(url);
    },
    enabled,
  });
}

export function useInviteCodes(vistoriadoraId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<InviteCode[]>({
    queryKey: [api.inviteCodes.list.path, vistoriadoraId],
    queryFn: () => {
      const params = vistoriadoraId ? `?vistoriadoraId=${vistoriadoraId}` : "";
      return authFetch(`${api.inviteCodes.list.path}${params}`);
    },
  });
}

export function useUserInvites() {
  const authFetch = useAuthFetch();
  return useQuery<UserInvite[]>({
    queryKey: [api.userInvites.list.path],
    queryFn: () => authFetch(api.userInvites.list.path),
  });
}

export function useVeiculos() {
  const authFetch = useAuthFetch();
  return useQuery<Veiculo[]>({
    queryKey: [api.veiculos.list.path],
    queryFn: () => authFetch(api.veiculos.list.path),
  });
}

export function useParceirosParaVeiculos(enabled = true) {
  const authFetch = useAuthFetch();
  return useQuery<Empresa[]>({
    queryKey: [api.veiculos.parceiros.path],
    queryFn: () => authFetch(api.veiculos.parceiros.path),
    enabled,
  });
}

export interface AuditoriaParams {
  entidade?: string;
  acao?: string;
  empresaId?: string;
  dataInicio?: string;
  dataFim?: string;
  page?: number;
}

export interface AuditoriaLog {
  id: string;
  user_id: string;
  user_nome: string;
  empresa_id: string;
  empresa_nome: string;
  entidade: string;
  entidade_id: string | null;
  entidade_descricao: string | null;
  acao: string;
  detalhes: Record<string, any> | null;
  criado_em: string;
}

export interface AuditoriaResult {
  data: AuditoriaLog[];
  total: number;
  page: number;
  limit: number;
}

export function useAuditoria(params: AuditoriaParams) {
  const authFetch = useAuthFetch();
  return useQuery<AuditoriaResult>({
    queryKey: [api.auditoria.list.path, params],
    queryFn: () => {
      const p = new URLSearchParams();
      if (params.entidade) p.set("entidade", params.entidade);
      if (params.acao) p.set("acao", params.acao);
      if (params.empresaId) p.set("empresaId", params.empresaId);
      if (params.dataInicio) p.set("dataInicio", params.dataInicio);
      if (params.dataFim) p.set("dataFim", params.dataFim);
      if (params.page && params.page > 1) p.set("page", String(params.page));
      const qs = p.toString();
      return authFetch(`${api.auditoria.list.path}${qs ? `?${qs}` : ""}`);
    },
  });
}

export function useNotificacoes() {
  const authFetch = useAuthFetch();
  return useQuery<Notificacao[]>({
    queryKey: [api.notificacoes.list.path],
    queryFn: () => authFetch(api.notificacoes.list.path),
    refetchInterval: 30_000,
  });
}

export function useMarcarNotificacaoLida() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(
        api.notificacoes.marcarLida.path.replace(":id", id),
        {
          method: "PATCH",
          headers,
        },
      );
      if (!res.ok) throw new Error("Erro ao marcar notificação");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.notificacoes.list.path] });
    },
  });
}

export function useMarcarTodasNotificacoesLidas() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(api.notificacoes.marcarTodasLidas.path, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw new Error("Erro ao marcar todas como lidas");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.notificacoes.list.path] });
    },
  });
}

export function useRTsByEmpresa(empresaId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<any[]>({
    queryKey: [api.usuarios.list.path, empresaId, "rt"],
    queryFn: () =>
      authFetch(`${api.usuarios.list.path}?empresaId=${encodeURIComponent(empresaId!)}`),
    enabled: !!empresaId,
    select: (users: any[]) => users.filter((u) => u.roles?.includes("rt")),
  });
}

export function useVeiculosSearch(q: string) {
  const authFetch = useAuthFetch();
  return useQuery<Veiculo[]>({
    queryKey: [api.veiculos.search.path, q],
    queryFn: () =>
      authFetch(`${api.veiculos.search.path}?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 3,
  });
}

export function useSinistroLocalizacao(veiculoId: string, enabled = true) {
  const authFetch = useAuthFetch();
  return useQuery<
    import("@/components/veiculos/SinistroLocalizacaoViewDialog").SinistroLocalizacaoData | null
  >({
    queryKey: [api.remontaSinistroLocalizacao.get.path, veiculoId],
    queryFn: () =>
      authFetch(
        api.remontaSinistroLocalizacao.get.path.replace(
          ":veiculoId",
          veiculoId,
        ),
      ),
    enabled: enabled && !!veiculoId,
  });
}

export function useRemontaCategorias(tipoVeiculo?: string) {
  const authFetch = useAuthFetch();
  return useQuery<any[]>({
    queryKey: [api.remontaCategorias.list.path, tipoVeiculo],
    queryFn: () =>
      authFetch(
        tipoVeiculo
          ? `${api.remontaCategorias.list.path}?tipoVeiculo=${tipoVeiculo}`
          : api.remontaCategorias.list.path,
      ),
  });
}

export function useRemontaElementos(categoriaId?: string, tipoVeiculo?: string) {
  const authFetch = useAuthFetch();
  return useQuery<any[]>({
    queryKey: [api.remontaElementos.list.path, categoriaId, tipoVeiculo],
    queryFn: () => {
      const params = new URLSearchParams();
      if (categoriaId) params.set("categoriaId", categoriaId);
      if (tipoVeiculo) params.set("tipoVeiculo", tipoVeiculo);
      const qs = params.toString();
      return authFetch(
        qs
          ? `${api.remontaElementos.list.path}?${qs}`
          : api.remontaElementos.list.path,
      );
    },
    enabled: !!(categoriaId || tipoVeiculo),
  });
}

// ── ReMonta — Laudos hooks ─────────────────────────────────────────────────

export function useRemontaLaudos(params?: {
  status?: string;
  rtId?: string;
  tipoVeiculo?: string;
  veiculoId?: string;
  enabled?: boolean;
}) {
  const authFetch = useAuthFetch();
  return useQuery<any[]>({
    queryKey: [api.remontaLaudos.list.path, params],
    queryFn: () => {
      const p = new URLSearchParams();
      if (params?.status) p.set("status", params.status);
      if (params?.rtId) p.set("rtId", params.rtId);
      if (params?.tipoVeiculo) p.set("tipoVeiculo", params.tipoVeiculo);
      if (params?.veiculoId) p.set("veiculoId", params.veiculoId);
      const qs = p.toString();
      return authFetch(qs ? `${api.remontaLaudos.list.path}?${qs}` : api.remontaLaudos.list.path);
    },
    enabled: params?.enabled ?? true,
  });
}

export function useRemontaLaudo(id?: string) {
  const authFetch = useAuthFetch();
  return useQuery<any>({
    queryKey: [api.remontaLaudos.get.path, id],
    queryFn: () => authFetch(`/api/remonta/laudos/${id}`),
    enabled: !!id,
  });
}

export function useRemontaItensLaudo(laudoId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<any[]>({
    queryKey: ["/api/remonta/laudos", laudoId, "itens"],
    queryFn: () => authFetch(`/api/remonta/laudos/${laudoId}/itens`),
    enabled: !!laudoId,
  });
}

export function useRemontaFotos(laudoId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<any[]>({
    queryKey: ["/api/remonta/laudos", laudoId, "fotos"],
    queryFn: () => authFetch(`/api/remonta/laudos/${laudoId}/fotos`),
    enabled: !!laudoId,
  });
}

export function useRemontaReparos(laudoId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<any[]>({
    queryKey: ["/api/remonta/laudos", laudoId, "reparos"],
    queryFn: () => authFetch(`/api/remonta/laudos/${laudoId}/reparos`),
    enabled: !!laudoId,
  });
}
